import React from "react";
import OnePanel from "./OnePanel";
import { LineBreak } from "./styles";
import styledComponents from "styled-components";

const ProductColor = styledComponents.span`
  color: #2a4169;
  font-weight: bold;
`;

const MarketingColor = styledComponents.span`
  color: #ff5252;
  font-weight: bold;
`;

const OperationsColor = styledComponents.span`
  color: #52c5cd;
  font-weight: bold;
`;

const CeoColor = styledComponents.span`
  color: #f9c151;
  font-weight: bold;
`;

const FinanceColor = styledComponents.span`
  color: #9900ff;
  font-weight: bold;
`;

const DarkGreenColor = styledComponents.span`
  color: #38761D;
  font-weight: bold;
`;

const DarkMagenta = styledComponents.span`
  color: #FF00FF;
  font-weight: bold;
`;

const UnorderedList = styledComponents.ul`
  padding: 0 0 0 40px;
  list-style: disc;
`;

const ListItem = styledComponents.li`
  font-size: 16px !important;
  line-height: 22.8571px;
`;

const SubTitle = styledComponents.h2`
  color: #2a4169;
  font-size: 18px;
  font-weight: bold;
`;

const calculate = initialDemand =>
  ((initialDemand.auto + initialDemand.mobile) / 1000000).toFixed(2);

const calculateTotalRevenue = (customerPreferences, customerDemands, activeRound = 1) => {
  const maxPrices = customerPreferences.filter(i => Number(i.round_number) === activeRound);
  const demands = customerDemands.filter(i => Number(i.round_number) === activeRound);
  const total = maxPrices.reduce((prev, obj) => {
    const customer = Number(obj.customer);
    const maxPrice = obj.max_price ? Number(obj.max_price) : 0;
    const demandObject = demands.find(i => Number(i.customer) === customer);
    const demand = demandObject ? Number(demandObject.demand) : 0;

    return prev + maxPrice * demand;
  }, 0);

  return `$${(total / 1000000).toFixed(0)}M`;
}

const ExecutiveSummary = ({ initialDemand, activeRound, customerPreferences, customerDemands }) => (
  <OnePanel title="EXECUTIVE SUMMARY">
    Fresh out of college, you and your close-knit group of friends made a bold
    choice during your senior year – to venture into the start-up world before
    considering a corporate job. With enthusiasm and determination, your team uncovered
    a promising prospect within the image sensor market. Image sensors, electronic devices
    responsible for transforming optical images into electronic signals, hold immense
    importance across diverse sectors, including the realms of smartphones and automobiles.
    <LineBreak />
    <ProductColor>You’re Entering the Image Sensor Market</ProductColor>
    <LineBreak />
    Image sensors have been on the market for quite some time. However, the image sensor
    technology as well as the product requirements (product features) demanded by customers
    are constantly evolving. Because of the expanding demand for mobile phones as well as
    the increasing interest in semi-autonomous vehicles, existing suppliers have not been
    able to meet the demand for image sensors. It is estimated that there will be an unmet
    demand for approximately {calculate(initialDemand)}M image sensors in the coming year and it is expected to
    grow further in the following years. Image sensor industry experts have estimated that
    this unmet demand translates to over {calculateTotalRevenue(customerPreferences, customerDemands)} in revenue.
    <LineBreak />
    In this simulation, you are going to assume that your team is entering the image sensor market to compete for the unmet
    demand in mobile and/or automobile customer segments. As such, you will need to make decisions related to capital,
    product development, product manufacturing, marketing, and sales, assuming that you will be in this business for
    multiple years. The game will be played in multiple rounds; each round corresponds to a year. You will be provided with
    product requirements as well as the expected volume requirements of different customers in each customer segment for
    each round.
    <LineBreak />
    <MarketingColor>Your Goal is to Capture Market Share and Hit Financial Targets</MarketingColor>
    <LineBreak />
    In each round, your team will compete with other teams to meet specific numerical goals. These goals vary from round to
    round, to reflect the success that a start-up business is expected to make over time. In each round, your team’s overall
    goal is to meet or exceed the numerical goals set for that round. The teams that achieve all goals will be designated as
    Market Leaders and will receive a score of 100. Other teams will earn designations and scores reflecting their levels of
    achievement. It is important to understand that the salability of your product depends on how your products compare with
    the competitors’ products (in terms of quality, price, promotion, and place) and you will not know the answer to this until
    the round ends—so it is full of ambiguity, just like the real business world.
    <LineBreak />
    <FinanceColor>You are Working As a Team And Will be Assigned A Role</FinanceColor>
    <LineBreak />
    This is a team-based simulation. Therefore, all team members must work in collaboration with the aim of achieving overall
    success. At the beginning of each round, each team member will be assigned the role of a divisional head or the CEO. Each
    divisional head is responsible to post decisions pertaining to their division. While the submission is done individually by
    divisional heads, decisions should be discussed and evaluated collaboratively, as each division’s decision will impact
    other decisions. It is also important to note that the CEO can post decisions related to all divisions, the CEO should not
    dominate the decisions; instead, the CEO is expected to strategize, forecast, coordinate, and collaborate.
    <LineBreak />
    <OperationsColor>You’ll Need to Leverage Research Data to Better Your Odds of Success</OperationsColor>
    <LineBreak />
    The Business Case and the Customer Analysis are the key resources to help you make informed decisions in Round 1. Therefore
    before making decisions, it’s in your best interest to conduct research and review The Business Case and the Customer
    Analysis. It’s also highly recommended that you refer back to research data as you make decisions. The Business Case gives
    you a general overview of the simulation (industry, market, product, operations, marketing, and capital sources), and The
    Customer Analysis provides greater insights into the customers and their preferences. The Business Case and Customer Analysis
    can be found on the game site located on the left nav panel under "Research".
    <LineBreak />
    <DarkGreenColor>Make & Post ALL Decisions Before Deadline</DarkGreenColor>
    <LineBreak />
    It’s highly recommended that your team make decisions in the following order: <ProductColor>Product</ProductColor> →&nbsp;
    <MarketingColor>Marketing</MarketingColor> →&nbsp;
    <OperationsColor>Operations</OperationsColor> →&nbsp;
    <CeoColor>CEO Projections</CeoColor> →&nbsp;
    <FinanceColor>Finance Decisions</FinanceColor>. It’s also required that your team POST ALL DECISIONS BEFORE THE ROUND DEADLINE. If decisions
    are not posted by the deadline, your team will likely perform poorly for that round.
    <LineBreak />
    <DarkMagenta>Review & Analyze Results</DarkMagenta>
    <LineBreak />
    After each round ends, you should review The Performance Feedback and Team Reports to gain a better understanding of your performance. The
    Performance Feedback report will provide personalized feedback. The Team Report will provide competitive analysis information. It compares
    your team’s performance in relation to your competitors and provides insights to help your team figure out what changes must be needed in
    order to do better in the next round.
    <LineBreak />
    <ProductColor>Tips to Getting Started</ProductColor>
    <LineBreak />
    <UnorderedList>
      <ListItem>Conduct Market Research to understand the market and customers</ListItem>
      <ListItem>Review and understand the financial targets you’ll need to hit each round</ListItem>
      <ListItem>Make Decisions in the following order: Product → Marketing → Operations → CEO Projections → Finance</ListItem>
      <ListItem>Post all decisions (CEO decision page)</ListItem>
    </UnorderedList>
    <LineBreak />
    <SubTitle>Welcome to Market Games! May the odds be ever in your favor!</SubTitle>
  </OnePanel>
);

export default ExecutiveSummary;



// WEBPACK FOOTER //
// ./src/components/bcase/ExecutiveSummary.js